import { createContext, useCallback, useContext, useState } from 'react';
import ToastContainer from './ToastContainer';

// ---------------------------------------------------------------------------------------------------------------------

/**
 * @typedef {('primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark')} ToastStyles
 */

/**
 * @typedef {Object} ToastConfig
 * @property {boolean | undefined} [sticky] - flag to maintain toast and not to removed automatically after timeout
 * @property {number | undefined} [timeout] - how many seconds the toast will be toast shown
 * @property {ToastStyles | undefined} [style] - the toast style
 * @property {string | undefined} [title] - the toast title
 * @property {string | undefined} [message] - the toast message
 */

// ---------------------------------------------------------------------------------------------------------------------

/**
 * @type {React.Context<{
 *    toasts: Array<{
 *        id: string;
 *        sticky: boolean;
 *        timeout: number;
 *        variant: ToastStyles;
 *        title?: string | undefined;
 *        message?: string | undefined;
 *    }>;
 *    showToast: function(ToastConfig): void;
 *    removeToast: function(string): void;
 * } | null>}
 */
const ToastContext = createContext(null);

const useInitContext = () => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (/*ToastConfig*/ cfg) => {
      if (
        typeof cfg != 'object' ||
        (typeof cfg.title != 'string' && typeof cfg.message != 'string') ||
        (typeof cfg.timeout == 'number' && (!Number.isFinite(cfg.timeout) || cfg.timeout <= 0))
      ) {
        return;
      }
      setToasts((prev) => [
        ...prev,
        {
          id: crypto.randomUUID?.() ?? `${Date.now()}_${Math.random()}`,
          sticky: typeof cfg.sticky == 'boolean' ? cfg.sticky : false,
          timeout:
            typeof cfg.timeout == 'number' ? cfg.timeout : 4000,
          variant: ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'].includes(cfg.style)
            ? cfg.style
            : 'primary',
          title: typeof cfg.title == 'string' ? cfg.title : undefined,
          message: typeof cfg.message == 'string' ? cfg.message : undefined,
        },
      ]);
    },
    [remove]
  );

  return {
    toasts: toasts,
    showToast: show,
    removeToast: remove,
  };
};

// ---------------------------------------------------------------------------------------------------------------------

/**
 *
 * @return {{
 *    toasts: Array<{
 *        id: string,
 *        sticky: boolean,
 *        timeout: number,
 *        variant: ToastStyles,
 *        title?: (string|undefined),
 *        message?: (string|undefined)}>,
 *   showToast: (function(ToastConfig): void),
 *   removeToast: (function(string): void)
 * }}
 */
export const useToastContext = () => {
  const ctx = useContext(ToastContext);
  if (ctx == null) {
    throw new Error('Toast context not initialized!');
  }
  return ctx;
};

export const ToastProvider = ({ children }) => {
  const value = useInitContext();
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={value.toasts} remove={value.removeToast} />
    </ToastContext.Provider>
  );
};

export const wrapWithToastProvider = (Component) => {
  return (props) => (
    <ToastProvider>
      <Component {...props} />
    </ToastProvider>
  );
};
